import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { MaskedLine, Reveal, ParallaxImage } from "../components/Motion";
import ContactFooter from "../components/ContactFooter";
import { fetchCaseStudies, fetchCaseStudy } from "../lib/api";

export default function CaseStudy() {
    const { slug } = useParams();
    const [selected, setSelected] = useState(null);

    const { data: cs, isLoading, isError } = useQuery({
        queryKey: ["case-study", slug],
        queryFn: () => fetchCaseStudy(slug),
        retry: false,
    });

    const { data: all } = useQuery({
        queryKey: ["case-studies"],
        queryFn: fetchCaseStudies,
    });

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setSelected(null);
            if (e.key === "ArrowRight" && cs?.gallery?.length) {
                setSelected((s) => {
                    if (!s) return s;
                    const idx = cs.gallery.findIndex((g) => g.src === s.src);
                    return cs.gallery[(idx + 1) % cs.gallery.length];
                });
            }
            if (e.key === "ArrowLeft" && cs?.gallery?.length) {
                setSelected((s) => {
                    if (!s) return s;
                    const idx = cs.gallery.findIndex((g) => g.src === s.src);
                    return cs.gallery[(idx - 1 + cs.gallery.length) % cs.gallery.length];
                });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [cs]);

    useEffect(() => {
        if (selected) {
            window.__lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            window.__lenis?.start();
            document.body.style.overflow = "";
        }
    }, [selected]);

    if (isLoading) {
        return (
            <main className="flex min-h-screen items-center justify-center">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-smoke">
                    Loading story…
                </p>
            </main>
        );
    }

    if (isError || !cs) {
        return (
            <main className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 text-center">
                <h1 className="font-display text-5xl font-light text-paper">
                    Story not found<span className="text-vermilion">.</span>
                </h1>
                <Link
                    to="/work"
                    data-testid="case-study-back-to-work"
                    className="border border-line px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
                >
                    Back to all work
                </Link>
            </main>
        );
    }

    const next = all && all.length > 1 ? all[(all.findIndex((c) => c.slug === slug) + 1) % all.length] : null;

    return (
        <main data-testid="case-study-page">
            <section className="px-5 md:px-10 pt-32 md:pt-40">
                <Link
                    to="/work"
                    data-testid="case-study-back-link"
                    className="group mb-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:text-vermilion"
                >
                    <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                    All work
                </Link>
                <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                    <span className="bg-vermilion px-2 py-1 text-paper">{cs.category}</span>
                    <span>{cs.client}</span>
                    <span>— {cs.year}</span>
                    {cs.attribution && (
                        <span className="border border-vermilion/60 px-2 py-1 text-vermilion" data-testid="case-study-attribution">
                            {cs.attribution}
                        </span>
                    )}
                    {cs.sample && <span className="border border-line px-2 py-1">Sample case study</span>}
                </div>
                <h1 className="display-hero font-display font-light text-paper mt-6 mb-10">
                    <MaskedLine>{cs.title}</MaskedLine>
                </h1>
                <p className="max-w-2xl font-display text-xl md:text-3xl font-light italic leading-snug text-paper/90">
                    "{cs.tagline}"
                </p>
            </section>

            <section className="px-0 mt-14 md:mt-20">
                <ParallaxImage
                    src={cs.hero_image}
                    alt={cs.title}
                    className="h-[60vh] md:h-[80vh] w-full border-y border-line"
                    strength={10}
                />
            </section>

            <section className="grid gap-12 px-5 md:px-10 py-24 md:py-32 lg:grid-cols-2 lg:gap-24">
                <Reveal>
                    <div>
                        <p className="eyebrow mb-8 flex items-center gap-3">
                            <span className="h-px w-10 bg-vermilion" /> The brief
                        </p>
                        <p className="text-base md:text-lg font-light leading-relaxed text-paper/90">
                            {cs.brief}
                        </p>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div>
                        <p className="eyebrow mb-8 flex items-center gap-3">
                            <span className="h-px w-10 bg-vermilion" /> The build
                        </p>
                        <p className="text-base md:text-lg font-light leading-relaxed text-smoke">
                            {cs.build}
                        </p>
                    </div>
                </Reveal>
            </section>

            {cs.gallery && cs.gallery.length > 0 && (
                <section className="px-5 md:px-10 pb-24 md:pb-32" data-testid="case-study-gallery">
                    <Reveal>
                        <p className="eyebrow mb-10 md:mb-14 flex items-center gap-3">
                            <span className="h-px w-10 bg-vermilion" /> Creative graphics — sport's biggest stages
                        </p>
                    </Reveal>
                    <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
                        {cs.gallery.map((g, i) => (
                            <Reveal key={g.src} delay={(i % 3) * 0.06} className="mb-6 break-inside-avoid">
                                <figure className="group overflow-hidden border border-line bg-card transition-colors duration-500 hover:border-vermilion">
                                    <button
                                        data-testid={`gallery-item-${i}`}
                                        onClick={() => setSelected(g)}
                                        aria-label={`View ${g.caption} full screen`}
                                        className="block w-full cursor-zoom-in"
                                    >
                                        <img
                                            src={g.src}
                                            alt={g.caption}
                                            loading="lazy"
                                            className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                        />
                                    </button>
                                    <figcaption className="border-t border-line px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-smoke">
                                        {g.caption}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            <section className="border-y border-line" data-testid="case-study-metrics">
                <div className="grid grid-cols-1 md:grid-cols-3">
                    {cs.metrics.map((m, i) => (
                        <div
                            key={m.label}
                            className={`px-5 md:px-10 py-14 md:py-20 ${i < 2 ? "max-md:border-b md:border-r border-line" : ""}`}
                        >
                            <p className="font-mono text-5xl md:text-6xl font-light text-vermilion">
                                {m.value}
                            </p>
                            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                                {m.label}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="border-t border-line px-5 md:px-10 py-4">
                    {cs.sample && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke/60">
                            * Sample figures — replace with real campaign results
                        </p>
                    )}
                </div>
            </section>

            {next && (
                <section className="px-5 md:px-10 py-20 md:py-28">
                    <Link
                        to={`/work/${next.slug}`}
                        data-testid="case-study-next"
                        className="group flex items-center justify-between gap-6 border border-line p-8 md:p-12 transition-colors duration-500 hover:border-vermilion"
                    >
                        <div>
                            <p className="eyebrow mb-4">Next project</p>
                            <h2 className="font-display text-4xl md:text-6xl font-light text-paper transition-colors duration-300 group-hover:text-vermilion">
                                {next.title}
                            </h2>
                        </div>
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-line transition-all duration-300 group-hover:border-vermilion group-hover:bg-vermilion">
                            <ArrowRight className="h-5 w-5 text-paper transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                    </Link>
                </section>
            )}

            <AnimatePresence>
                {selected && (
                    <motion.div
                        data-testid="poster-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md md:p-10"
                        onClick={() => setSelected(null)}
                    >
                        <button
                            data-testid="lightbox-close"
                            onClick={() => setSelected(null)}
                            aria-label="Close full screen view"
                            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center border border-line bg-ink/60 text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        {cs.gallery.length > 1 && (
                            <>
                                <button
                                    data-testid="lightbox-prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const idx = cs.gallery.findIndex((g) => g.src === selected.src);
                                        setSelected(cs.gallery[(idx - 1 + cs.gallery.length) % cs.gallery.length]);
                                    }}
                                    aria-label="Previous poster"
                                    className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-line bg-ink/60 text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion md:left-6"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button
                                    data-testid="lightbox-next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const idx = cs.gallery.findIndex((g) => g.src === selected.src);
                                        setSelected(cs.gallery[(idx + 1) % cs.gallery.length]);
                                    }}
                                    aria-label="Next poster"
                                    className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-line bg-ink/60 text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion md:right-6"
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </>
                        )}
                        <motion.figure
                            key={selected.src}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="flex max-h-full flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={selected.src}
                                alt={selected.caption}
                                className="max-h-[82vh] max-w-full object-contain"
                            />
                            <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-smoke" data-testid="lightbox-caption">
                                {selected.caption}
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>

            <ContactFooter />
        </main>
    );
}
